import { TrackingContext } from '@change-corgi/core/react/tracking';
import { DesignSystemLegacy } from '@change-corgi/design-system-legacy/core';
import { Box, Container } from '@change-corgi/design-system-legacy/layout';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { sunriseTheme } from '@change-corgi/design-system-legacy/theme';

import { StripeLoader } from 'src/app/shared/components/payments';
import { PaypalProvider } from 'src/app/shared/hooks/payments';

import { Footer } from './components/Footer';
import { GeneralErrorMessage } from './components/GeneralErrorMessage';
import { Header } from './components/Header';
import { MembershipForm } from './container/MembershipForm';
import { MembershipModalStateContextProvider, useMembershipModalState } from './modalContext/modalState';
import type { ModalOptions, TrackingProperties } from './shared/types';
import { SunriseHoldoutExperimentContextProvider } from './sunriseHoldoutExperiment/components/SunriseHoldoutExperimentContextProvider';
import { useSunriseHoldoutExperiment } from './sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

type Options = ModalOptions & {
	properties: TrackingProperties;
	sunriseDoubleHoldoutExperiment?: { variant: string | undefined };
};

export const useMembershipAskModal = createModalHook<Options, { accepted: boolean; amount: number }>({
	name: 'MembershipModal',
	closeButton: false,
	closeOnEscape: false,
	closeOnClickOutside: false,
	variant: 'fullPage',
	body: function MembershipModalBody({ closeModal, options }) {
		const [{ amount }] = useMembershipModalState();
		const { isSunriseEnabled } = useSunriseHoldoutExperiment({ isSunriseEnabled: options.isSunriseEnabled });

		return (
			<DesignSystemLegacy theme={isSunriseEnabled ? sunriseTheme : undefined}>
				<Container sx={{ pt: ['0%', '5%', '5%'] }}>
					<Box sx={{ m: 'auto', px: [0, 32], width: ['100%', '80%', '50%'] }}>
						<Header />
						<MembershipForm
							onAccept={() => closeModal({ accepted: true, amount })}
							onDecline={() => closeModal({ accepted: false, amount })}
						/>
						<GeneralErrorMessage />
						<Footer />
					</Box>
				</Container>
			</DesignSystemLegacy>
		);
	},
	decorator: ({ children, options: { properties, sunriseDoubleHoldoutExperiment, ...modalState } }) => (
		<MembershipModalStateContextProvider {...modalState}>
			<TrackingContext properties={properties}>
				<StripeLoader>
					<PaypalProvider>
						<SunriseHoldoutExperimentContextProvider variant={sunriseDoubleHoldoutExperiment?.variant}>
							{children}
						</SunriseHoldoutExperimentContextProvider>
					</PaypalProvider>
				</StripeLoader>
			</TrackingContext>
		</MembershipModalStateContextProvider>
	),
});
